import type { SearchResultsPayload } from "types";

export const COVOMO_TARIFF_MANAGER_THEME = "covomo_tariff_manager";

export function resolveThemeKey(theme: unknown): string | undefined {
  if (theme == null) return undefined;
  return Array.isArray(theme) ? String(theme[0]) : String(theme);
}

export function isCovomoTariffManagerTheme(theme: unknown): boolean {
  return resolveThemeKey(theme) === COVOMO_TARIFF_MANAGER_THEME;
}

/** URL/store origin gate for tariff-manager-only session fields & UI (markings, compare ids). */
export function isTariffManagerOrigin(origin: unknown): boolean {
  return String(origin ?? "").trim() === "tariff-manager";
}

export function hasTariffManagerSearchFilters(
  payload: Pick<SearchResultsPayload, "filters">,
): boolean {
  return payload.filters.some((filter) => {
    const value = filter?.value;
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
}

/** Tariff-manager searches must be user-initiated and include at least one filter value. */
export function shouldSkipTariffManagerSearchWithoutFilters(
  theme: unknown,
  payload: Pick<SearchResultsPayload, "filters">,
): boolean {
  return (
    isCovomoTariffManagerTheme(theme) && !hasTariffManagerSearchFilters(payload)
  );
}

type MissingValueStore = {
  theme: unknown;
  setMissingValue: (missing: boolean) => void;
};

type SearchCacheStore = {
  clearSuccessfulSearchCache: () => void;
};

type TariffResultsStore = {
  tariffs: unknown[];
  setTariffs: (tariffs: unknown[]) => void;
  setTotalTariffs: (total: number) => void;
  setNumberOfFetchedTariffs: (count: number) => void;
  setMainLoader: (loading: boolean) => void;
};

/** Keep previous results visible while the user edits the mask in tariff-manager theme. */
export function applyMissingValueForSearchMask(
  rootStore: MissingValueStore,
  tariffsCount: number,
  missing: boolean,
): void {
  if (
    missing &&
    isCovomoTariffManagerTheme(rootStore.theme) &&
    tariffsCount > 0
  ) {
    return;
  }

  rootStore.setMissingValue(missing);
}

/** Tariff-manager backoffice: hide footer/sidebar legal UI and contract cancellation. */
export function shouldShowLegalAndCancellationLinks(theme: unknown): boolean {
  return !isCovomoTariffManagerTheme(theme);
}

/** Tariff-manager backoffice: hide field help icons (?) in the search mask.
 * When `tm_edit` deep-links are active, keep tooltips visible so labels stay usable.
 */
export function shouldShowSearchFieldTooltips(
  theme: unknown,
  tmEditEnabled = false,
): boolean {
  if (tmEditEnabled) return true;
  return !isCovomoTariffManagerTheme(theme);
}

type LegalUiRootStore = {
  setShowLegalLinks: (value: string) => void;
};

export function applyTariffManagerLegalUiVisibility(
  theme: unknown,
  rootStore?: LegalUiRootStore,
): void {
  if (typeof document === "undefined") {
    return;
  }

  if (!isCovomoTariffManagerTheme(theme)) {
    return;
  }

  document.body.style.setProperty("--show-legal-links", "none");
  rootStore?.setShowLegalLinks("false");
}

/** Backoffice tariff-manager: re-run search even when the mask values are unchanged. */
export function shouldBypassSearchParamsDedup(
  theme: unknown,
  forceRepeatSearch: boolean,
): boolean {
  return forceRepeatSearch && isCovomoTariffManagerTheme(theme);
}

/** Tariff-manager search button: skip dedup and debounce delays. */
export function shouldRunTariffManagerButtonSearchImmediately(
  theme: unknown,
  forceRepeatSearch: boolean,
): boolean {
  return shouldBypassSearchParamsDedup(theme, forceRepeatSearch);
}

/** Clear stale results when the user submits a new tariff-manager search. */
export function beginTariffManagerSearch(
  rootStore: MissingValueStore,
  tariffStore: TariffResultsStore,
  searchStore: SearchCacheStore,
): void {
  if (!isCovomoTariffManagerTheme(rootStore.theme)) {
    return;
  }

  tariffStore.setTariffs([]);
  tariffStore.setTotalTariffs(0);
  tariffStore.setNumberOfFetchedTariffs(0);
  rootStore.setMissingValue(false);
  tariffStore.setMainLoader(true);
  searchStore.clearSuccessfulSearchCache();
}
